import { useEffect, useRef, useState } from 'react';
import { CoinIcon } from './icons';

export interface BucksCounterProps {
  /** Current balance; changes roll the odometer up/down. */
  value: number;
  /** Accessible name, e.g. t('ec2.a11y.bucksBalance', {amount}). */
  label: string;
  /** BCP47 locale used for the group separator. */
  locale?: string;
  className?: string;
  /** Tap target (brief: balance is tappable → ledger). */
  onClick?: () => void;
}

const DIGITS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Odometer balance counter (brief §5 "Bucks earned counter"): digits roll up over
 * --duration-grand with the spring ease; the coin does one 12° tilt-and-settle.
 * Reduced motion: the counter jumps straight to its final value (CSS also kills
 * the reel transition via the tokens file).
 */
export function BucksCounter({ value, label, locale = 'en', className = '', onClick }: BucksCounterProps) {
  const [displayValue, setDisplayValue] = useState(value);
  const [settling, setSettling] = useState(false);
  const previous = useRef(value);

  useEffect(() => {
    if (previous.current === value) return;
    previous.current = value;
    if (prefersReducedMotion()) {
      setDisplayValue(value);
      return;
    }
    setSettling(true);
    setDisplayValue(value);
    const timer = setTimeout(() => setSettling(false), 600);
    return () => clearTimeout(timer);
  }, [value]);

  const parts = new Intl.NumberFormat(locale).formatToParts(Math.max(0, Math.floor(displayValue)));

  // Key each digit reel by its PLACE VALUE from the right (d0 = ones, d1 = tens, …)
  // so a reel always owns one column and only rolls within it. When the balance
  // crosses a digit-count boundary (9→10, 99→100) the new leading digit mounts as
  // a fresh reel instead of an existing reel being reused for a different place
  // value (which would visibly spin the wrong way). Separators are keyed by the
  // place of the digit group to their right for the same reason.
  const totalDigits = parts.reduce((n, p) => n + (p.type === 'integer' ? p.value.length : 0), 0);
  const pieces: { key: string; place?: number; digit?: string; sep?: string }[] = [];
  let remaining = totalDigits;
  for (const part of parts) {
    if (part.type === 'integer') {
      for (const digit of part.value) {
        remaining -= 1;
        pieces.push({ key: `d${remaining}`, place: remaining, digit });
      }
    } else {
      pieces.push({ key: `s${remaining}`, sep: part.value });
    }
  }

  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      role={onClick ? undefined : 'img'}
      onClick={onClick}
      aria-label={label}
      data-testid="ec-bucks-counter"
      className={
        `inline-flex items-center gap-1.5 font-display text-[22px] font-[750] text-bucks tabular-nums ` +
        `${onClick ? 'cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ' : ''}` +
        className
      }
    >
      <span
        aria-hidden
        className={`inline-flex transition-transform duration-(--duration-slow) ${settling ? 'rotate-12' : 'rotate-0'}`}
      >
        <CoinIcon size={20} />
      </span>
      <span aria-hidden className="flex overflow-hidden leading-none" data-testid="ec-odometer">
        {pieces.map((piece) =>
          piece.digit !== undefined ? (
            <span
              key={piece.key}
              data-place={piece.place}
              className="relative block h-[1em] w-[0.62em] overflow-hidden"
            >
              <span
                className="ec-odometer-reel absolute left-0 top-0 flex flex-col items-center"
                style={{ transform: `translateY(-${Number(piece.digit)}em)` }}
                data-digit={piece.digit}
              >
                {DIGITS.map((d) => (
                  <span key={d} className="block h-[1em]">
                    {d}
                  </span>
                ))}
              </span>
            </span>
          ) : (
            <span key={piece.key} className="block h-[1em]">
              {piece.sep}
            </span>
          ),
        )}
      </span>
    </Tag>
  );
}
